import { useState } from "react"
import {
  ArrowLeftRight,
  Building2,
  Check,
  ChevronLeft,
  ChevronRight,
  Cylinder,
  MapPin,
  Settings,
  Wrench,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import { T, softVariant } from "../theme"
import {
  Avatar,
  BottomSheet,
  MButton,
  MCard,
  MChip,
  MField,
  MStageBadge,
  MTagChip,
  SectionLabel,
  Touchable,
} from "../primitives"
import { timeAgo } from "../utils"
import { SearchablePicker } from "../SearchablePicker"
import { MoveStageForm } from "./MoveStageForm"
import type { MoveStageFormValues } from "./MoveStageForm"
import type {
  Customer,
  Location as LocationRef,
  MobileAsset,
  MobileEvent,
  Product,
  StageWithRules,
  Tag,
} from "../types"

type SubmitResult =
  | { ok: true }
  | { ok: false; errors: Record<string, string> }

interface AssetDetailScreenProps {
  asset: MobileAsset
  events: MobileEvent[]
  /** Stages with rule metadata, same shape used by the desktop move dialog. */
  stages: StageWithRules[]
  products: Product[]
  customers: Customer[]
  availableTags: Tag[]
  locations: LocationRef[]
  onClose: () => void
  /** Submit a single-asset stage move. Errors come back via the returned promise. */
  onMove: (values: MoveStageFormValues) => Promise<SubmitResult>
  /** Submit a single-asset location move. */
  onMoveLocation: (input: { location_id: string; notes: string }) => Promise<SubmitResult>
  onToggleTag?: (tagId: Tag["id"]) => void
}

type DetailTab = "info" | "history" | "tags"

export function AssetDetailScreen({
  asset,
  events,
  stages,
  products,
  customers,
  availableTags,
  locations,
  onClose,
  onMove,
  onMoveLocation,
  onToggleTag,
}: AssetDetailScreenProps) {
  const { t } = useTranslation()
  const [moveOpen, setMoveOpen] = useState(false)
  const [locationOpen, setLocationOpen] = useState(false)
  const [tab, setTab] = useState<DetailTab>("info")
  const [moved, setMoved] = useState<{ kind: "stage" | "location"; label: string } | null>(null)
  const [moving, setMoving] = useState(false)
  const [moveErrors, setMoveErrors] = useState<Record<string, string>>({})
  const [locationMoving, setLocationMoving] = useState(false)
  const [locationErrors, setLocationErrors] = useState<Record<string, string>>({})

  const handleMove = async (values: MoveStageFormValues, target: StageWithRules) => {
    setMoving(true)
    setMoveErrors({})
    const result = await onMove(values)
    setMoving(false)
    if (!result.ok) {
      setMoveErrors(result.errors)
      return
    }
    setMoveOpen(false)
    setMoved({ kind: "stage", label: target.name })
    setTimeout(() => setMoved(null), 1800)
  }

  const handleMoveLocation = async (location: LocationRef, notes: string) => {
    setLocationMoving(true)
    setLocationErrors({})
    const result = await onMoveLocation({ location_id: String(location.id), notes })
    setLocationMoving(false)
    if (!result.ok) {
      setLocationErrors(result.errors)
      return
    }
    setLocationOpen(false)
    setMoved({ kind: "location", label: location.name })
    setTimeout(() => setMoved(null), 1800)
  }

  const tagSet = new Set(asset.tags.map((t) => t.id))

  return (
    <div
      style={{
        position: "absolute", inset: 0, zIndex: 40,
        background: T.bg,
        display: "flex", flexDirection: "column",
        animation: "mScreenIn 280ms cubic-bezier(.22,1,.36,1)",
      }}
    >
      <header
        style={{
          position: "sticky", top: 0, zIndex: 5,
          padding: "10px 8px",
          background: T.surface,
          borderBottom: `1px solid ${T.borderSoft}`,
          display: "flex", alignItems: "center", gap: 4,
        }}
      >
        <Touchable
          onClick={onClose}
          style={{
            width: 44, height: 44, display: "grid", placeItems: "center", borderRadius: 999,
          }}
        >
          <ChevronLeft size={22} color={T.ink} />
        </Touchable>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: T.fontMono,
              fontSize: 10,
              color: T.inkSoft,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            {t("mobile.assetDetail.eyebrow")}
          </div>
          <div
            style={{
              fontFamily: T.fontMono,
              fontSize: 16,
              fontWeight: 700,
              color: T.ink,
              letterSpacing: 0.3,
              marginTop: 1,
            }}
          >
            {asset.code}
          </div>
        </div>
      </header>

      <div style={{ flex: 1, overflow: "auto", paddingBottom: 100 }}>
        {/* Hero card */}
        <div
          style={{
            margin: "16px 16px 0",
            padding: 18,
            background: T.surface,
            borderRadius: T.radius,
            border: `1px solid ${T.border}`,
            borderTop: `3px solid ${asset.stage.color}`,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0, right: 0,
              background: T.ink,
              color: T.accent,
              padding: "4px 10px",
              fontFamily: T.fontMono,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1.2,
              textTransform: "uppercase",
            }}
          >
            {asset.code}
          </div>
          <div style={{ position: "relative", display: "flex", gap: 16, marginTop: 14 }}>
            <div
              style={{
                width: 64, height: 64,
                borderRadius: T.radius,
                background: T.surfaceAlt,
                border: `1px solid ${T.border}`,
                display: "grid", placeItems: "center", flexShrink: 0,
              }}
            >
              <Cylinder size={30} color={T.ink} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontFamily: T.fontMono,
                  fontSize: 10,
                  color: T.inkSoft,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                  fontWeight: 600,
                }}
              >
                {t("mobile.assetDetail.productLabel")}
              </div>
              <div
                style={{
                  fontFamily: T.fontDisplay,
                  fontSize: 18,
                  fontWeight: 700,
                  color: T.ink,
                  letterSpacing: -0.2,
                  marginTop: 2,
                }}
              >
                {asset.product?.name || "—"}
              </div>
              {asset.container?.name && (
                <div
                  style={{
                    fontFamily: T.fontMono,
                    fontSize: 12,
                    color: T.inkSoft,
                    marginTop: 4,
                    letterSpacing: 0.4,
                  }}
                >
                  {asset.container.name}
                </div>
              )}
              <div style={{ marginTop: 10 }}>
                <MStageBadge stage={asset.stage} />
              </div>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginTop: 16,
              paddingTop: 14,
              borderTop: `1px solid ${T.divider}`,
            }}
          >
            <QuickStat
              icon={Building2}
              label={t("mobile.assetDetail.customerLabel")}
              value={asset.customer?.name || "—"}
            />
            <QuickStat
              icon={MapPin}
              label={t("mobile.assetDetail.locationLabel")}
              value={asset.location?.name || "—"}
            />
          </div>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            gap: 4,
            padding: "16px 16px 0",
            borderBottom: `1px solid ${T.borderSoft}`,
            marginTop: 16,
          }}
        >
          {(
            [
              ["info", t("mobile.assetDetail.tabInfo")],
              ["history", t("mobile.assetDetail.tabHistory")],
              ["tags", t("mobile.assetDetail.tabTags")],
            ] as Array<[DetailTab, string]>
          ).map(([k, label]) => (
            <Touchable
              key={k}
              onClick={() => setTab(k)}
              style={{
                padding: "10px 14px",
                position: "relative",
                fontFamily: T.fontBody,
                fontSize: 14,
                fontWeight: 600,
                color: tab === k ? T.ink : T.inkSoft,
              }}
            >
              {label}
              {tab === k && (
                <div
                  style={{
                    position: "absolute",
                    left: 14,
                    right: 14,
                    bottom: -1,
                    height: 2,
                    background: T.accent,
                    borderRadius: "2px 2px 0 0",
                  }}
                />
              )}
            </Touchable>
          ))}
        </div>

        <div style={{ padding: "16px" }}>
          {tab === "info" && <InfoTab asset={asset} t={t} />}
          {tab === "history" && <HistoryTab events={events} t={t} />}
          {tab === "tags" && (
            <TagsTab
              assetTagIds={tagSet}
              availableTags={availableTags}
              onToggle={onToggleTag}
              t={t}
            />
          )}
        </div>
      </div>

      {/* Sticky bottom CTAs — move stage / move location */}
      <div
        style={{
          position: "absolute",
          left: 0, right: 0, bottom: 0,
          padding: "12px 16px 22px",
          background: T.surface,
          borderTop: `1px solid ${T.borderSoft}`,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
        }}
      >
        <MButton
          variant="ghost"
          fullWidth
          leftIcon={MapPin}
          onClick={() => setLocationOpen(true)}
        >
          {t("mobile.assetDetail.moveLocation")}
        </MButton>
        <MButton
          variant="accent"
          fullWidth
          leftIcon={ArrowLeftRight}
          onClick={() => setMoveOpen(true)}
        >
          {t("mobile.assetDetail.moveStage")}
        </MButton>
      </div>

      {/* Move sheet — same rules logic as the desktop MoveStageDialog */}
      <BottomSheet
        open={moveOpen}
        onClose={() => (moving ? undefined : setMoveOpen(false))}
        title={t("mobile.assetDetail.moveStage")}
      >
        <MoveStageForm
          assetType={asset.assetable_type}
          stages={stages}
          products={products}
          customers={customers}
          tags={availableTags}
          currentStage={asset.stage}
          initial={{
            stage_id: "",
            product_id: asset.product?.id ? String(asset.product.id) : "",
            customer_id: asset.customer?.id ? String(asset.customer.id) : "",
            batch_number: asset.batch_number || "",
            expiration_date: asset.expiration_date || "",
            tag_ids: asset.tags.map((tag) => tag.id),
          }}
          errors={moveErrors}
          busy={moving}
          onSubmit={handleMove}
        />
      </BottomSheet>

      {/* Move location sheet */}
      <BottomSheet
        open={locationOpen}
        onClose={() => (locationMoving ? undefined : setLocationOpen(false))}
        title={t("mobile.assetDetail.moveLocation")}
      >
        <MoveLocationForm
          currentLocation={asset.location || null}
          locations={locations}
          errors={locationErrors}
          busy={locationMoving}
          onSubmit={handleMoveLocation}
        />
      </BottomSheet>

      {/* Toast */}
      {moved && (
        <div
          style={{
            position: "absolute",
            left: 16, right: 16, bottom: 90, zIndex: 60,
            animation: "mtoastIn 220ms cubic-bezier(.22,1,.36,1)",
          }}
        >
          <div
            style={{
              background: T.ink,
              color: "#fff",
              borderRadius: T.radius,
              padding: "12px 14px",
              display: "flex",
              alignItems: "center",
              gap: 12,
              boxShadow: T.shadowLg,
              borderLeft: `3px solid ${T.success}`,
            }}
          >
            <span
              style={{
                width: 26, height: 26, borderRadius: 999, background: T.success,
                display: "grid", placeItems: "center",
              }}
            >
              <Check size={14} color="#fff" strokeWidth={2.5} />
            </span>
            <span style={{ fontSize: 14, fontWeight: 500, flex: 1 }}>
              {moved.kind === "stage"
                ? t("mobile.assetDetail.stageUpdatedTo")
                : t("mobile.assetDetail.locationUpdatedTo")}{" "}
              <strong>{moved.label}</strong>
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

interface QuickStatProps {
  icon: LucideIcon
  label: string
  value: string
}

function QuickStat({ icon: Icon, label, value }: QuickStatProps) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", minWidth: 0 }}>
      <div
        style={{
          width: 28, height: 28, background: T.surfaceAlt,
          border: `1px solid ${T.border}`,
          display: "grid", placeItems: "center", flexShrink: 0,
        }}
      >
        <Icon size={14} color={T.inkSoft} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: T.fontMono,
            fontSize: 9.5,
            color: T.inkSoft,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 13.5,
            color: T.ink,
            fontWeight: 500,
            marginTop: 2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {value}
        </div>
      </div>
    </div>
  )
}

function InfoTab({ asset, t }: { asset: MobileAsset; t: TFunction }) {
  return (
    <MCard padded={false}>
      <div style={{ padding: "0 16px" }}>
        <MField label={t("mobile.assetDetail.fields.code")} value={asset.code} mono />
        <MField label={t("mobile.assetDetail.fields.product")} value={asset.product?.name} />
        <MField label={t("mobile.assetDetail.fields.container")} value={asset.container?.name} />
        <MField label={t("mobile.assetDetail.fields.customer")} value={asset.customer?.name} />
        <MField label={t("mobile.assetDetail.fields.currentLocation")} value={asset.location?.name} />
        {asset.bought_at && (
          <MField
            label={t("mobile.assetDetail.fields.boughtAt")}
            value={new Date(asset.bought_at).toLocaleDateString()}
            mono
          />
        )}
        {asset.serial_number && (
          <MField label={t("mobile.assetDetail.fields.serialNumber")} value={asset.serial_number} mono />
        )}
        {asset.batch_number && (
          <MField label={t("mobile.assetDetail.fields.batchNumber")} value={asset.batch_number} mono />
        )}
        {asset.expiration_date && (
          <MField
            label={t("mobile.assetDetail.fields.expirationDate")}
            value={new Date(`${asset.expiration_date}T00:00:00`).toLocaleDateString()}
            mono
          />
        )}
      </div>
    </MCard>
  )
}

function HistoryTab({ events, t }: { events: MobileEvent[]; t: TFunction }) {
  if (events.length === 0) {
    return (
      <MCard>
        <div style={{ padding: 16, textAlign: "center", color: T.inkSoft, fontSize: 14 }}>
          {t("mobile.assetDetail.noEvents")}
        </div>
      </MCard>
    )
  }
  return (
    <div style={{ position: "relative" }}>
      <div
        aria-hidden
        style={{
          position: "absolute", left: 19, top: 8, bottom: 8,
          width: 2, background: T.borderSoft,
        }}
      />
      {events.map((e, i) => (
        <HistoryEvent key={e.id ?? i} event={e} t={t} />
      ))}
    </div>
  )
}

const EVENT_META: Record<string, { Icon: LucideIcon; color: string; labelKey: string }> = {
  stage:       { Icon: ArrowLeftRight, color: T.accent, labelKey: "mobile.assetDetail.eventStage" },
  move:        { Icon: MapPin,         color: "#0891B2", labelKey: "mobile.assetDetail.eventLocation" },
  maintenance: { Icon: Wrench,         color: "#B45309", labelKey: "mobile.assetDetail.eventMaintenance" },
  creation:    { Icon: Settings,       color: T.inkSoft, labelKey: "mobile.assetDetail.eventCreation" },
}

function HistoryEvent({ event: e, t }: { event: MobileEvent; t: TFunction }) {
  const meta = EVENT_META[e.type] || EVENT_META.stage
  const Icon = meta.Icon
  const dateLabel = e.created_at
    ? formatEventDate(e.created_at)
    : e.when || timeAgo(e.created_at, t)

  return (
    <div style={{ position: "relative", paddingLeft: 48, marginBottom: 14 }}>
      <div
        style={{
          position: "absolute",
          left: 8, top: 12,
          width: 24, height: 24, borderRadius: 999,
          background: T.surface,
          border: `2px solid ${meta.color}`,
          display: "grid", placeItems: "center",
          boxShadow: `0 0 0 3px ${T.bg}`,
          zIndex: 2,
        }}
      >
        <Icon size={11} color={meta.color} />
      </div>
      <div
        style={{
          background: T.surface,
          border: `1px solid ${T.borderSoft}`,
          borderRadius: T.radius,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "8px 12px",
            background: softVariant(meta.color, 0.08),
            borderBottom: `1px solid ${T.borderSoft}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontFamily: T.fontMono,
            fontSize: 10,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          <span style={{ color: meta.color }}>{t(meta.labelKey)}</span>
          <span style={{ color: T.inkSoft }}>{dateLabel}</span>
        </div>
        <div style={{ padding: 12 }}>
          {e.type === "stage" && e.from_stage && e.to_stage && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
                marginBottom: e.meta ? 8 : 0,
              }}
            >
              <MStageBadge stage={e.from_stage} size="sm" variant="soft" />
              <ChevronRight size={11} color={T.inkSoft} />
              <MStageBadge stage={e.to_stage} size="sm" />
            </div>
          )}
          {e.type === "move" && (e.from_location || e.to_location) && (
            <div
              style={{
                fontSize: 13,
                color: T.ink,
                fontWeight: 500,
                display: "flex",
                gap: 6,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <MapPin size={12} color={T.inkSoft} />
              {e.from_location?.name || "—"}
              <ChevronRight size={11} color={T.inkSoft} />
              <span style={{ color: meta.color, fontWeight: 600 }}>
                {e.to_location?.name || "—"}
              </span>
            </div>
          )}
          {e.note && (
            <div style={{ fontSize: 13, color: T.inkSoft, marginTop: 6, fontStyle: "italic" }}>
              {e.note}
            </div>
          )}
          {e.meta && (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
              {Object.entries(e.meta).map(([k, v]) => (
                <div key={k} style={{ fontSize: 12 }}>
                  <span style={{ color: T.inkSoft }}>{k}: </span>
                  <span style={{ color: T.ink, fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>
          )}
          {e.who && (
            <div
              style={{
                marginTop: 10,
                paddingTop: 8,
                borderTop: `1px dashed ${T.borderSoft}`,
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 11.5,
                color: T.inkSoft,
              }}
            >
              <Avatar name={e.who} size={18} />
              <span>
                {t("mobile.assetDetail.by")} <strong style={{ color: T.ink }}>{e.who}</strong>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function formatEventDate(iso: string) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  // Defer to the runtime locale (set elsewhere by the i18n config).
  const date = d.toLocaleDateString()
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  return `${date} · ${time}`
}

interface TagsTabProps {
  assetTagIds: Set<Tag["id"]>
  availableTags: Tag[]
  onToggle?: (tagId: Tag["id"]) => void
  t: TFunction
}

function TagsTab({ assetTagIds, availableTags, onToggle, t }: TagsTabProps) {
  const assigned = availableTags.filter((tag) => assetTagIds.has(tag.id))
  return (
    <MCard>
      <SectionLabel>{t("mobile.assetDetail.tagsAssigned")}</SectionLabel>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
        {assigned.length === 0 && (
          <span style={{ fontSize: 13, color: T.inkSoft }}>{t("mobile.assetDetail.noTags")}</span>
        )}
        {assigned.map((tag) => (
          <MTagChip key={tag.id} tag={tag} />
        ))}
      </div>
      <div style={{ height: 1, background: T.borderSoft, margin: "16px 0" }} />
      <SectionLabel>{t("mobile.assetDetail.tagsAddRemove")}</SectionLabel>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {availableTags.map((tag) => (
          <MChip
            key={tag.id}
            active={assetTagIds.has(tag.id)}
            leftDot
            color={tag.color}
            onClick={() => onToggle?.(tag.id)}
          >
            {tag.name}
          </MChip>
        ))}
      </div>
    </MCard>
  )
}

interface MoveLocationFormProps {
  currentLocation: LocationRef | null
  locations: LocationRef[]
  errors?: Record<string, string>
  busy?: boolean
  onSubmit: (location: LocationRef, notes: string) => void
}

function MoveLocationForm({
  currentLocation,
  locations,
  errors,
  busy,
  onSubmit,
}: MoveLocationFormProps) {
  const { t } = useTranslation()
  const [target, setTarget] = useState<LocationRef | null>(null)
  const [notes, setNotes] = useState("")

  const errorMessages = errors ? Object.values(errors).filter(Boolean) : []

  return (
    <div style={{ padding: "8px 16px 24px" }}>
      {currentLocation && (
        <div
          style={{
            padding: "12px 14px",
            marginBottom: 16,
            background: T.surfaceAlt,
            borderRadius: T.radius,
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontFamily: T.fontMono,
            fontSize: 12,
            letterSpacing: 0.6,
            color: T.inkSoft,
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          <MapPin size={14} color={T.inkSoft} />
          {t("mobile.assetDetail.currentLocationStamp")}&nbsp;
          <span style={{ color: T.ink }}>{currentLocation.name}</span>
        </div>
      )}

      {errorMessages.length > 0 && (
        <div
          style={{
            padding: "10px 12px",
            marginBottom: 16,
            background: T.dangerSoft,
            border: `1px solid ${T.danger}`,
            borderRadius: T.radius,
            fontSize: 13,
            color: T.danger,
            lineHeight: 1.45,
          }}
        >
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {errorMessages.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </div>
      )}

      <SectionLabel>
        {t("mobile.assetDetail.locationDestination")}{" "}
        <span style={{ color: T.accent, fontWeight: 700 }}>*</span>
      </SectionLabel>
      <div style={{ marginBottom: 16 }}>
        <SearchablePicker
          value={target ? String(target.id) : ""}
          onChange={(id) => setTarget(locations.find((l) => String(l.id) === id) || null)}
          options={locations}
          title={t("mobile.assetDetail.locationDestination")}
          placeholder={t("mobile.moveStageForm.selectPlaceholder")}
        />
      </div>

      <SectionLabel>{t("mobile.moveStageForm.noteOptional")}</SectionLabel>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={2}
        placeholder={t("mobile.assetDetail.locationNotePlaceholder")}
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "10px 12px",
          marginBottom: 16,
          borderRadius: T.radius,
          border: `1px solid ${T.border}`,
          background: T.surface,
          fontFamily: T.fontBody,
          // 16px keeps iOS from auto-zooming on focus. See SearchBar.
          fontSize: 16,
          color: T.ink,
          outline: "none",
          resize: "none",
        }}
      />

      <MButton
        variant="accent"
        fullWidth
        size="lg"
        leftIcon={MapPin}
        disabled={!target || busy}
        onClick={() => target && onSubmit(target, notes)}
      >
        {t("mobile.assetDetail.confirmLocation")}
      </MButton>
    </div>
  )
}
